import React, { useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { Image, Upload, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import OnboardingNavigationBar from '../OnboardingNavigationBar';

const LogoUploadStep = ({
    companyId,
    logoFile,
    setLogoFile,
    logoPreview,
    setLogoPreview,
    onNext,
    onBack,
    onSkip,
    saving,
    setSaving,
    markStepCompleted,
    alreadyCompleted,
}) => {
    const handleFileSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Ogiltigt filformat. Använd PNG, JPEG, SVG eller WebP.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('Filen är för stor. Max 2MB.');
            return;
        }

        setLogoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setLogoPreview(reader.result);
        reader.readAsDataURL(file);
    }, [setLogoFile, setLogoPreview]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            // Simulate via input handler logic
            const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Ogiltigt filformat. Använd PNG, JPEG, SVG eller WebP.');
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Filen är för stor. Max 2MB.');
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    }, [setLogoFile, setLogoPreview]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const clearLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };

    const handleUpload = async () => {
        if (!logoFile) {
            onSkip();
            return;
        }

        try {
            setSaving(true);
            const formData = new FormData();
            formData.append('file', logoFile);

            const url = getApiUrl(`${companyId}/logo`);
            await axiosInstance.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Logotyp uppladdad');
            markStepCompleted();
            onNext();
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(error.response?.data?.message || 'Kunde inte ladda upp logotyp');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <style>{`
                .logo-upload-step {
                    max-width: 500px;
                    margin: 0 auto;
                }
                .step-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 0.5rem;
                }
                .step-header-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: var(--rgba-primary-1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                }
                .step-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                .step-description {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-bottom: 1.5rem;
                }
                .logo-dropzone {
                    border: 2px dashed #e2e8f0;
                    border-radius: 12px;
                    padding: 2rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.15s;
                    background: #fafafa;
                    position: relative;
                }
                .logo-dropzone:hover {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }
                .logo-dropzone-icon {
                    color: #94a3b8;
                    margin-bottom: 0.75rem;
                }
                .logo-dropzone-text {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-bottom: 0.25rem;
                }
                .logo-dropzone-hint {
                    font-size: 0.75rem;
                    color: #94a3b8;
                }
                .logo-preview-container {
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1.5rem;
                    text-align: center;
                    background: white;
                    position: relative;
                }
                .logo-preview-img {
                    max-width: 200px;
                    max-height: 120px;
                    object-fit: contain;
                    border-radius: 8px;
                }
                .logo-preview-name {
                    font-size: 0.8rem;
                    color: #64748b;
                    margin-top: 0.75rem;
                }
                .logo-clear-btn {
                    position: absolute;
                    top: 0.5rem;
                    right: 0.5rem;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 50%;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #64748b;
                    transition: all 0.15s;
                }
                .logo-clear-btn:hover {
                    background: #fee2e2;
                    color: #ef4444;
                }
                .logo-skip-note {
                    font-size: 0.8rem;
                    color: #94a3b8;
                    text-align: center;
                    margin-top: 1rem;
                }
            `}</style>
            <div className="logo-upload-step">
                <div className="step-header">
                    <div className="step-header-icon">
                        <Image size={20} />
                    </div>
                    <h3 className="step-title">Logotyp</h3>
                </div>
                <p className="step-description">
                    Ladda upp din företagslogotyp. Den visas på fakturor, offerter och andra dokument.
                </p>

                {alreadyCompleted && (
                    <Alert variant="success" style={{ fontSize: '0.85rem', borderRadius: '8px', marginBottom: '1.25rem' }}>
                        <CheckCircle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                        En logotyp är redan uppladdad. Du kan byta ut den eller gå vidare.
                    </Alert>
                )}

                {logoPreview ? (
                    <div className="logo-preview-container">
                        <button className="logo-clear-btn" onClick={clearLogo} title="Ta bort">
                            <X size={14} />
                        </button>
                        <img src={logoPreview} alt="Logo preview" className="logo-preview-img" />
                        <div className="logo-preview-name">{logoFile?.name}</div>
                    </div>
                ) : (
                    <div
                        className="logo-dropzone"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => document.getElementById('logo-file-input').click()}
                    >
                        <div className="logo-dropzone-icon">
                            <Upload size={32} />
                        </div>
                        <div className="logo-dropzone-text">
                            Dra hit din logotyp eller klicka för att välja
                        </div>
                        <div className="logo-dropzone-hint">
                            PNG, JPEG, SVG eller WebP. Max 2MB.
                        </div>
                        <input
                            id="logo-file-input"
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                    </div>
                )}

                <p className="logo-skip-note">
                    Detta steg är valfritt. Du kan ladda upp en logotyp när som helst under Inställningar.
                </p>

                <OnboardingNavigationBar
                    onBack={onBack}
                    onNext={handleUpload}
                    onSkip={onSkip}
                    showSkip={!logoFile}
                    nextLabel={logoFile ? 'Ladda upp & fortsätt' : 'Fortsätt'}
                    saving={saving}
                />
            </div>
        </>
    );
};

export default LogoUploadStep;
